import { REQ } from "constants/api/requests"
import { IResponse } from "constants/interfaces"

import useApi from "util/api"

import {
  BettingStatsResponse,
  DepositorStatsResponse,
  FilteredTransacation,
  GgrSummaryResponse,
  GgrSummaryType,
  ITotalDataService,
  StateResponse,
  StatsResponse,
  TransactionFilterType,
  TransactionStatsResponse,
  YearlyStatsResponse
} from "./types"

class TotalDataService implements ITotalDataService {
  public async getTransactionsTable(
    page: number,
    pageSize: number,
    id: number,
    filter: TransactionFilterType,
    sortRow?: string,
    sortOrder?: string
  ): Promise<FilteredTransacation | []> {
    const params = {
      page,
      pageSize,
      type: filter?.type || null,
      status: filter?.status || null,
      sortRow,
      sortOrder
    }

    const { data } = await useApi.get<IResponse<FilteredTransacation>>(REQ.ADMIN_DASHBOARD.TRANSACTIONS.GET, { params: { ...params } })

    if (data?.success) {
      return data?.response
    }

    return []
  }

  public async getUserCount(dates: (Date | null)[]): Promise<StateResponse | undefined> {
    const params = {
      startDate: dates?.[0] || null,
      endDate: dates?.[1] || null
    }

    const { data } = await useApi.get<IResponse<StateResponse>>(REQ.ADMIN_DASHBOARD.USERS_COUNT, { params })

    if (data?.success) {
      return data?.response
    }
  }

  public async getTotalDeposit(dates: (Date | null)[]): Promise<StateResponse | undefined> {
    const params = {
      startDate: dates?.[0] || null,
      endDate: dates?.[1] || null
    }

    const { data } = await useApi.get<IResponse<StateResponse>>(REQ.ADMIN_DASHBOARD.TRANSACTIONS.DEPOSIT_TOTAL, { params })

    if (data?.success) {
      return data?.response
    }
  }

  public async getTotalWithdrawal(dates: (Date | null)[]): Promise<StateResponse | undefined> {
    const params = {
      startDate: dates?.[0] || null,
      endDate: dates?.[1] || null
    }

    const { data } = await useApi.get<IResponse<StateResponse>>(REQ.ADMIN_DASHBOARD.TRANSACTIONS.WITHDRAWAL_TOTAL, {
      params
    })

    if (data?.success) {
      return data?.response
    }
  }

  public async getTotalPendingWithdrawal(dates: (Date | null)[]): Promise<StateResponse | undefined> {
    const params = {
      startDate: dates?.[0] || null,
      endDate: dates?.[1] || null
    }

    const { data } = await useApi.get<IResponse<StateResponse>>(REQ.ADMIN_DASHBOARD.TRANSACTIONS.WITHDRAWAL_PENDING, {
      params
    })

    if (data?.success) {
      return data?.response
    }
  }

  public async getTotalWager(dates: (Date | null)[]): Promise<StateResponse | undefined> {
    const params = {
      startDate: dates?.[0] || null,
      endDate: dates?.[1] || null
    }

    const { data } = await useApi.get<IResponse<StateResponse>>(REQ.ADMIN_DASHBOARD.TRANSACTIONS.BETS_TOTAL, { params })

    if (data?.success) {
      return data?.response
    }
  }

  public async getTotalWins(dates: (Date | null)[]): Promise<StateResponse | undefined> {
    const params = {
      startDate: dates?.[0] || null,
      endDate: dates?.[1] || null
    }

    const { data } = await useApi.get<IResponse<StateResponse>>(REQ.ADMIN_DASHBOARD.TRANSACTIONS.WINS_TOTAL, { params })

    if (data?.success) {
      return data?.response
    }
  }

  public async getTotalRakeback(dates: (Date | null)[]): Promise<StateResponse | undefined> {
    const params = {
      startDate: dates?.[0] || null,
      endDate: dates?.[1] || null
    }

    const { data } = await useApi.get<IResponse<StateResponse>>(REQ.ADMIN_DASHBOARD.TRANSACTIONS.RAKEBACK_TOTAL, { params })

    if (data?.success) {
      return data?.response
    }
  }

  public async getTotalAffiliate(dates: (Date | null)[]): Promise<StateResponse | undefined> {
    const params = {
      startDate: dates?.[0] || null,
      endDate: dates?.[1] || null
    }

    const { data } = await useApi.get<IResponse<StateResponse>>(REQ.ADMIN_DASHBOARD.TRANSACTIONS.AFFILIATE_TOTAL, { params })

    if (data?.success) {
      return data?.response
    }
  }

  public async getTotalPromoCode(dates: (Date | null)[]): Promise<StateResponse | undefined> {
    const params = {
      startDate: dates?.[0] || null,
      endDate: dates?.[1] || null
    }

    const { data } = await useApi.get<IResponse<StateResponse>>(REQ.ADMIN_DASHBOARD.TRANSACTIONS.PROMOCODE_TOTAL, { params })

    if (data?.success) {
      return data?.response
    }
  }

  public async getStats(dates: (Date | null)[], betTypes?: string[], signal?: AbortSignal): Promise<StatsResponse | undefined> {
    const params: Record<string, any> = {
      startDate: dates?.[0] || null,
      endDate: dates?.[1] || null
    }

    if (betTypes?.length) params.betTypes = betTypes

    const { data, status } = await useApi.get<IResponse<StatsResponse>>(REQ.ADMIN_DASHBOARD.STATS, { params, signal })

    if (data?.success) return data?.response
    else if (status === 409) throw new Error("Fetch operation was aborted")
  }

  public async getStatsBetting(
    dates: (Date | null)[],
    betTypes?: string[],
    signal?: AbortSignal
  ): Promise<BettingStatsResponse | undefined> {
    const params: Record<string, any> = {
      startDate: dates?.[0] || null,
      endDate: dates?.[1] || null
    }
    if (betTypes?.length) params.betTypes = betTypes
    const { data } = await useApi.get<IResponse<BettingStatsResponse>>(REQ.ADMIN_DASHBOARD.STATS_BETTING, {
      params,
      signal
    })
    if (data?.success) return data?.response
  }

  public async getStatsTransactions(dates: (Date | null)[], signal?: AbortSignal): Promise<TransactionStatsResponse | undefined> {
    const params = { startDate: dates?.[0] || null, endDate: dates?.[1] || null }
    const { data } = await useApi.get<IResponse<TransactionStatsResponse>>(REQ.ADMIN_DASHBOARD.STATS_TRANSACTIONS, {
      params,
      signal
    })
    if (data?.success) return data?.response
  }

  public async getStatsDepositors(dates: (Date | null)[], signal?: AbortSignal): Promise<DepositorStatsResponse | undefined> {
    const params = { startDate: dates?.[0] || null, endDate: dates?.[1] || null }
    const { data } = await useApi.get<IResponse<DepositorStatsResponse>>(REQ.ADMIN_DASHBOARD.STATS_DEPOSITORS, {
      params,
      signal
    })
    if (data?.success) return data?.response
  }

  public async getStatsByYear(dates: (Date | null)[], q?: Record<string, any>): Promise<YearlyStatsResponse[] | undefined> {
    const { data, status } = await useApi.get<IResponse<YearlyStatsResponse[]>>(REQ.ADMIN_DASHBOARD.STATS_BY_YEAR, {
      params: { startDate: dates[0], endDate: dates[1], ...q }
    })

    if (data?.success) return data.response
    else if (status === 409) throw new Error("Fetch operation was aborted")
  }

  public async getStatsByMonth(dates: (Date | null)[], q?: Record<string, any>): Promise<YearlyStatsResponse[] | undefined> {
    const { data, status } = await useApi.get<IResponse<YearlyStatsResponse[]>>(REQ.ADMIN_DASHBOARD.STATS_BY_MONTH, {
      params: { startDate: dates[0], endDate: dates[1], ...q }
    })

    if (data?.success) return data.response
    else if (status === 409) throw new Error("Fetch operation was aborted")
  }

  public async getStatsByDay(dates: (Date | null)[], q?: Record<string, any>): Promise<YearlyStatsResponse[] | undefined> {
    const { data, status } = await useApi.get<IResponse<YearlyStatsResponse[]>>(REQ.ADMIN_DASHBOARD.STATS_BY_DAY, {
      params: { startDate: dates[0], endDate: dates[1], ...q }
    })

    if (data?.success) return data.response
    else if (status === 409) throw new Error("Fetch operation was aborted")
  }

  public async getStatsByHour(dates: (Date | null)[], q?: Record<string, any>): Promise<YearlyStatsResponse[] | undefined> {
    const { data, status } = await useApi.get<IResponse<YearlyStatsResponse[]>>(REQ.ADMIN_DASHBOARD.STATS_BY_HOUR, {
      params: { startDate: dates[0], endDate: dates[1], ...q }
    })

    if (data?.success) return data.response
    else if (status === 409) throw new Error("Fetch operation was aborted")
  }
}

export const totalDataService = new TotalDataService()

export async function fetchGgrByType(
  type: GgrSummaryType,
  dates: (Date | null)[],
  sortOrder: "asc" | "desc" = "desc"
): Promise<Partial<GgrSummaryResponse> | undefined> {
  const params = {
    startDate: dates?.[0] || null,
    endDate: dates?.[1] || null,
    type,
    sortOrder
  }

  const { data } = await useApi.get<IResponse<Partial<GgrSummaryResponse>>>(REQ.ADMIN_DASHBOARD.GGR_SUMMARY, { params })

  if (data?.success) return data?.response
}
